/**
 * Postmaster V2 — ComplianceStatus tab.
 *
 * One Card per finanztrends domain showing the 8 Gmail/Yahoo sender-compliance
 * requirement verdicts (COMPLIANT vs NEEDS_WORK) plus the deliverability
 * status verdict (state + reason — Google's Developer-Preview "Deliverability
 * Analysis"). The reason enum surfaces inline when a verdict is broken — this
 * is the diagnostic surface that answers "what specifically is Google
 * flagging?" — V1 had no equivalent.
 *
 * Backed by GET /api/postmaster/v2/compliance (postmaster-v2.ts route),
 * written daily by `postmaster_v2_daily_sync` cron (services/PostmasterServiceV2.ts).
 *
 * The deliverability verdict is a binary state with a diagnostic reason — it
 * is NOT a replacement for the retired 4-tier domain/IP reputation (Google
 * removed those with the v1 shutdown 2026-08-27, no API equivalent exists).
 * Historic context 2026-06-23: all 8 requirements COMPLIANT while reputation
 * sat at LOW for 30+ days — the LOW was engagement-driven, not setup-driven;
 * today the USER_FEEDBACK_* reasons carry that engagement signal.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { CheckCircleOutline, ErrorOutline, HelpOutline } from '@mui/icons-material';
import { api } from '../../helpers/apiUtils';

// Column-name pairs mirror the postmaster_v2_compliance_snapshot schema.
// Order matters — drives display order in the Card body.
const REQUIREMENTS: Array<{
  key: string;
  label: string;
  statusCol: string;
  reasonCol: string;
}> = [
  {
    key: 'spf_and_dkim',
    label: 'SPF & DKIM',
    statusCol: 'spf_and_dkim_status',
    reasonCol: 'spf_and_dkim_reason',
  },
  {
    key: 'dmarc_alignment',
    label: 'DMARC-Ausrichtung',
    statusCol: 'dmarc_alignment_status',
    reasonCol: 'dmarc_alignment_reason',
  },
  {
    key: 'dmarc_policy',
    label: 'DMARC-Policy',
    statusCol: 'dmarc_policy_status',
    reasonCol: 'dmarc_policy_reason',
  },
  {
    key: 'encryption',
    label: 'TLS-Verschlüsselung',
    statusCol: 'encryption_status',
    reasonCol: 'encryption_reason',
  },
  {
    key: 'user_reported_spam',
    label: 'User-Spam-Rate',
    statusCol: 'user_reported_spam_status',
    reasonCol: 'user_reported_spam_reason',
  },
  {
    key: 'dns_records',
    label: 'DNS-Einträge',
    statusCol: 'dns_records_status',
    reasonCol: 'dns_records_reason',
  },
  {
    key: 'one_click_unsub',
    label: 'One-Click-Abmelden',
    statusCol: 'one_click_unsub_status',
    reasonCol: 'one_click_unsub_reason',
  },
  {
    key: 'honor_unsub',
    label: 'Abmeldungen respektieren',
    statusCol: 'honor_unsub_status',
    reasonCol: 'honor_unsub_reason',
  },
];

interface ComplianceRow {
  domain: string;
  snapshot_date: string;
  updated_at: string;
  [key: string]: string | null;
}

type Verdict = 'COMPLIANT' | 'NEEDS_WORK' | null;

function verdictIcon(status: Verdict): React.ReactNode {
  if (status === 'COMPLIANT')
    return <CheckCircleOutline sx={{ fontSize: 18, color: 'success.main' }} />;
  if (status === 'NEEDS_WORK') return <ErrorOutline sx={{ fontSize: 18, color: 'warning.main' }} />;
  return <HelpOutline sx={{ fontSize: 18, color: 'text.disabled' }} />;
}

function verdictColor(status: Verdict): string {
  if (status === 'COMPLIANT') return 'success.main';
  if (status === 'NEEDS_WORK') return 'warning.main';
  return 'text.disabled';
}

// Prefix-matching variant for the Verlauf table, where Zustellbarkeit rows
// carry compound strings like "COMPLIANT (USER_FEEDBACK_LOW)" — exact-match
// verdictColor would render them text.disabled.
function verdictColorLoose(s: string | null): string {
  if (s?.startsWith('COMPLIANT')) return 'success.main';
  if (s?.startsWith('NEEDS_WORK')) return 'warning.main';
  return 'text.disabled';
}

// Reason enum of the deliverability status verdict (v2 discovery rev 20260825).
// Unknown values render raw — Google may extend the Preview enum.
const DELIVERABILITY_REASON_LABELS: Record<string, string> = {
  USER_FEEDBACK_POSITIVE: 'Nutzer wollen diese Mails (positives Engagement)',
  USER_FEEDBACK_LOW: 'Nutzer reagieren kaum auf die Mails (niedriges Engagement)',
  USER_FEEDBACK_NEGATIVE: 'Nutzer wollen diese Mails nicht (negatives Feedback)',
  SPAM_RATE_HIGH: 'Spam-Rate über 0,1 %',
  SMTP_ERRORS_HIGH: 'Viele Nachrichten mit Zustellfehlern',
  SENDER_NOT_COMPLIANT: 'Sender-Requirements nicht erfüllt',
  MESSAGE_VOLUME_LOW: 'Zu wenig ausgehendes Mailvolumen für eine Bewertung',
};

function deliverabilityReasonLabel(reason: string | null): string | null {
  if (!reason) return null;
  return DELIVERABILITY_REASON_LABELS[reason] ?? reason;
}

interface PostmasterV2ComplianceTabProps {
  // null = no filter applied yet (parent still loading the domain list).
  // Empty array = user explicitly selected nothing → render nothing.
  // Populated array = restrict to these domains.
  selectedDomains: string[] | null;
}

const PostmasterV2ComplianceTab: React.FC<PostmasterV2ComplianceTabProps> = ({
  selectedDomains,
}) => {
  const [rows, setRows] = useState<ComplianceRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        // Default: last 30 days, all domains the writer has populated.
        const res = await api.get('/api/postmaster/v2/compliance', { params: { days: 30 } });
        if (cancelled) return;
        setRows(res.data.data || []);
      } catch (err) {
        if (!cancelled) setError(`Konnte Compliance-Daten nicht laden: ${(err as Error).message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply the central domain filter. null = no filter yet (parent loading).
  const filteredRows = useMemo(() => {
    if (selectedDomains === null) return rows;
    const set = new Set(selectedDomains);
    return rows.filter((r) => set.has(r.domain));
  }, [rows, selectedDomains]);

  // Latest row per domain — the Card surface always shows current state.
  const latestByDomain = useMemo(() => {
    const map = new Map<string, ComplianceRow>();
    for (const r of filteredRows) {
      const existing = map.get(r.domain);
      if (!existing || r.snapshot_date > existing.snapshot_date) map.set(r.domain, r);
    }
    return Array.from(map.values()).sort((a, b) => a.domain.localeCompare(b.domain));
  }, [filteredRows]);

  // Verlauf: status changes per (domain, requirement) over the 30d window.
  // Only surface rows where status DIFFERS from the previous-day's status.
  const statusChanges = useMemo(() => {
    type Change = {
      domain: string;
      stat_date: string;
      requirement: string;
      from: string | null;
      to: string | null;
    };
    const byDomain = new Map<string, ComplianceRow[]>();
    for (const r of filteredRows) {
      if (!byDomain.has(r.domain)) byDomain.set(r.domain, []);
      byDomain.get(r.domain)!.push(r);
    }
    const changes: Change[] = [];
    // Deliverability changes on the PAIR (status, reason) — status-only would
    // swallow COMPLIANT/USER_FEEDBACK_LOW → COMPLIANT/USER_FEEDBACK_POSITIVE,
    // which is exactly the engagement shift worth surfacing.
    const withReason = (row: ComplianceRow): string | null => {
      const status = (row.deliverability_status as string | null) ?? null;
      const reason = (row.deliverability_reason as string | null) ?? null;
      if (!status && !reason) return null;
      return reason ? `${status ?? '–'} (${reason})` : status;
    };
    Array.from(byDomain.entries()).forEach(([domain, list]) => {
      const sorted = [...list].sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date));
      for (let i = 1; i < sorted.length; i++) {
        for (const req of REQUIREMENTS) {
          const prevStatus = (sorted[i - 1][req.statusCol] as string | null) ?? null;
          const currStatus = (sorted[i][req.statusCol] as string | null) ?? null;
          if (prevStatus !== currStatus) {
            changes.push({
              domain,
              stat_date: sorted[i].snapshot_date,
              requirement: req.label,
              from: prevStatus,
              to: currStatus,
            });
          }
        }
        const prevPair = withReason(sorted[i - 1]);
        const currPair = withReason(sorted[i]);
        if (prevPair !== currPair) {
          changes.push({
            domain,
            stat_date: sorted[i].snapshot_date,
            requirement: 'Zustellbarkeit',
            from: prevPair,
            to: currPair,
          });
        }
      }
    });
    return changes.sort((a, b) => b.stat_date.localeCompare(a.stat_date));
  }, [filteredRows]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (latestByDomain.length === 0) {
    return (
      <Alert severity="info">
        Noch keine V2-Compliance-Daten vorhanden. Der tägliche Sync (04:20 Berlin) füllt die Tabelle
        ab dem ersten Lauf nach Deploy.
      </Alert>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Strategischer Hinweis:</strong> Wenn ALLE Requirements COMPLIANT sind, aber das
        Zustellbarkeits-Verdict auf USER_FEEDBACK_LOW/NEGATIVE steht, ist es eine{' '}
        <em>Engagement-Krise, keine Setup-Krise</em>. Compliance prüft &quot;Threshold
        unterschritten?&quot;; das Verdict reagiert auf Nutzer-Feedback und Komplaints. (Die
        frühere Domain-/IP-Reputation hat Google mit der V1-Abschaltung am 27.08.2026 ersatzlos
        eingestellt — Historie bis 23.08. steht weiter in der Traffic-Sektion.)
      </Alert>

      <Stack spacing={2}>
        {latestByDomain.map((r) => {
          // Tally all three buckets explicitly — null (unknown) must not
          // collapse into "all OK", otherwise missing Google data renders
          // visually identical to an explicit COMPLIANT verdict.
          let compliantCount = 0;
          let brokenCount = 0;
          let unknownCount = 0;
          for (const req of REQUIREMENTS) {
            const status = (r[req.statusCol] as string | null) ?? null;
            if (status === 'COMPLIANT') compliantCount++;
            else if (status === 'NEEDS_WORK') brokenCount++;
            else unknownCount++;
          }
          let overallChip: React.ReactNode;
          if (brokenCount > 0) {
            overallChip = (
              <Chip
                label={`${brokenCount} Anforderung${brokenCount > 1 ? 'en' : ''} verletzt`}
                color="warning"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            );
          } else if (unknownCount > 0) {
            overallChip = (
              <Chip
                label={`${compliantCount} OK · ${unknownCount} unbekannt`}
                color="default"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            );
          } else {
            overallChip = (
              <Chip
                label="Alle Anforderungen OK"
                color="success"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            );
          }
          return (
            <Card key={r.domain}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                  }}
                >
                  <Typography variant="h6">{r.domain}</Typography>
                  {overallChip}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  Snapshot: {r.snapshot_date}
                </Typography>
                {(() => {
                  const dStatus = (r.deliverability_status as string | null) ?? null;
                  const dReason = (r.deliverability_reason as string | null) ?? null;
                  if (!dStatus && !dReason) return null;
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 1.25,
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                      }}
                    >
                      {verdictIcon(dStatus as Verdict)}
                      <Typography variant="body2" sx={{ minWidth: 220, fontWeight: 600 }}>
                        Zustellbarkeit (Deliverability)
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: verdictColor(dStatus as Verdict),
                          fontWeight: 600,
                          minWidth: 110,
                        }}
                      >
                        {dStatus ?? '–'}
                      </Typography>
                      {dReason && (
                        <Typography variant="caption" color="text.secondary">
                          {deliverabilityReasonLabel(dReason)}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
                <Stack spacing={0.75}>
                  {REQUIREMENTS.map((req) => {
                    const status = (r[req.statusCol] as string | null) ?? null;
                    const reason = (r[req.reasonCol] as string | null) ?? null;
                    return (
                      <Box
                        key={req.key}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                      >
                        {verdictIcon(status as Verdict)}
                        <Typography variant="body2" sx={{ minWidth: 220 }}>
                          {req.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: verdictColor(status as Verdict),
                            fontWeight: 600,
                            minWidth: 110,
                          }}
                        >
                          {status ?? '–'}
                        </Typography>
                        {reason && (
                          <Typography variant="caption" color="text.secondary">
                            {reason}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Verlauf (letzte 30 Tage)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Nur Tage mit Statuswechsel pro (Domain × Anforderung). Leerer Bereich = keine Änderungen.
        </Typography>
        {statusChanges.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Keine Statuswechsel im 30-Tage-Fenster.
          </Typography>
        ) : (
          <TableContainer sx={{ backgroundColor: '#ffffff', maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Datum', 'Domain', 'Anforderung', 'Von', 'Auf'].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {statusChanges.map((c, i) => (
                  <TableRow key={`${c.domain}-${c.stat_date}-${c.requirement}-${i}`}>
                    <TableCell>{c.stat_date}</TableCell>
                    <TableCell>{c.domain}</TableCell>
                    <TableCell>{c.requirement}</TableCell>
                    <TableCell sx={{ color: verdictColorLoose(c.from) }}>
                      {c.from ?? '–'}
                    </TableCell>
                    <TableCell sx={{ color: verdictColorLoose(c.to), fontWeight: 600 }}>
                      {c.to ?? '–'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default PostmasterV2ComplianceTab;
